import {createStore} from 'zustand/vanilla';

import {
  INITIAL_BILLING_STATE,
  selectWBAgentBillingAccess,
  type WBAgentBillingState,
} from './access';

/**
 * createBillingStore builds an independent billing store. Production uses the
 * shared `billingStore` singleton; tests create their own for isolation.
 */
export function createBillingStore() {
  const store = createStore<WBAgentBillingState>()(() => INITIAL_BILLING_STATE);
  let refreshHandle: (() => Promise<void>) | null = null;

  return {
    /** The vanilla store, for React bindings (subscribe/getState). */
    store,

    /** setBilling overwrites the snapshot; state holds only billing fields. */
    setBilling: (next: WBAgentBillingState): void => {
      store.setState(next, true);
    },

    /** reset returns to the fail-closed initial state. */
    reset: (): void => {
      store.setState(INITIAL_BILLING_STATE, true);
    },

    /** isAccessible reads the current verdict for non-React guards. */
    isAccessible: (): boolean =>
      selectWBAgentBillingAccess(store.getState()).accessible,

    /**
     * registerRefresh installs an action that re-pulls billing from source and
     * returns a disposer. Mirrors the controller store's registerHandle.
     */
    registerRefresh: (fn: () => Promise<void>): (() => void) => {
      refreshHandle = fn;
      return () => {
        if (refreshHandle === fn) {
          refreshHandle = null;
        }
      };
    },

    /** refresh invokes the registered action and resolves when it ends. */
    refresh: (): Promise<void> => refreshHandle?.() ?? Promise.resolve(),
  };
}

/** BillingStore is one independent billing store instance. */
export type BillingStore = ReturnType<typeof createBillingStore>;

/** billingStore is the shared, app-wide billing store instance. */
export const billingStore = createBillingStore();
